import React, { useState, useEffect, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import { useTeams } from '../../hooks/useTeams';
import tempUploadService from '../../services/tempUploadService';
import './CreateMinisiteModal.css';

/**
 * CreateMinisiteModal
 *
 * Logo upload uses the TempUpload pattern:
 *  1. onChange → POST /api/temp-upload → get { id, file_url }
 *  2. Show preview immediately from file_url
 *  3. On submit → send logo_temp_upload_id to create/update API
 *  4. Backend calls moveTempToPermanent(id) → stores path, deletes temp record
 *
 * Props:
 *   onClose()                 — close the modal
 *   onSubmit(payload)         — async fn in parent; throws on 422
 *   loading    bool           — disable buttons while API is in-flight
 *   fieldErrors {}            — { title: ["msg"], ... } from parent after 422
 *   initialData {}            — pre-fill when editing
 *   isEdit     bool
 */
const CreateMinisiteModal = ({ onClose, onSubmit, loading, fieldErrors = {}, initialData, isEdit }) => {
    const { teams } = useTeams();
    const logoInputRef = useRef(null);

    const [form, setForm] = useState({
        title: '',
        team_id: '',
        email: '',
        phone: '',
        address: '',
        logoPreview: null,  // URL to display in the upload box
        logoTempUploadId: null,  // ID returned from /api/temp-upload
    });

    const [logoUploading, setLogoUploading] = useState(false); // spinner while POSTing to temp-upload
    const [logoError, setLogoError] = useState(null);  // error from temp-upload itself

    // Local errors: synced from fieldErrors prop (parent's 422 response)
    const [errors, setErrors] = useState({});
    useEffect(() => { setErrors(fieldErrors); }, [fieldErrors]);

    // Pre-fill when editing
    useEffect(() => {
        if (initialData) {
            setForm((prev) => ({
                ...prev,
                title: initialData.title || '',
                team_id: initialData.team_id || '',
                email: initialData.contact_email || initialData.email || '',
                phone: initialData.contact_phone || initialData.phone || '',
                address: initialData.contact_address || initialData.address || '',
                logoPreview: initialData.logo_url || null,
                // NOTE: no logoTempUploadId on edit — user must re-upload to change logo
            }));
        }
    }, [initialData]);

    // ESC to close
    useEffect(() => {
        const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
    };

    /**
     * On file select → immediately POST to /api/temp-upload.
     * Show a spinner inside the upload box while this is in-flight.
     * On success: store the temp ID + preview URL in state.
     */
    const handleLogoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Instant local preview while upload is in-flight
        const localPreview = URL.createObjectURL(file);
        setForm((prev) => ({ ...prev, logoPreview: localPreview, logoTempUploadId: null }));
        setLogoUploading(true);
        setLogoError(null);
        if (errors.logo) setErrors((err) => ({ ...err, logo: null }));

        try {
            const response = await tempUploadService.upload(file);
            // api.js interceptor returns response.data (body), then .data is the temp_upload object
            const tempUpload = response.data;
            setForm((prev) => ({
                ...prev,
                logoPreview: tempUpload.file_url,
                logoTempUploadId: tempUpload.id,
            }));
        } catch (err) {
            setLogoError('Logo upload failed. Please try again.');
            setForm((prev) => ({ ...prev, logoPreview: null, logoTempUploadId: null }));
        } finally {
            setLogoUploading(false);
        }

        // Reset the input so the same file can be re-selected if needed
        e.target.value = '';
    };

    const handleSubmit = () => {
        const payload = {
            title: form.title,
            team_id: form.team_id || null,
            contact_email: form.email || null,
            contact_phone: form.phone || null,
            contact_address: form.address || null,
            logo_temp_upload_id: form.logoTempUploadId || null,
        };
        onSubmit(payload);
    };

    const isLogoSet = !!form.logoPreview;

    return (
        <div className="ms-cm-overlay" onClick={onClose}>
            <div className="ms-cm-container" onClick={(e) => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className="ms-cm-header">
                    <h2 className="ms-cm-title">
                        {isEdit ? 'Edit Minisite' : 'Create Minisite'}
                    </h2>
                    <button
                        type="button"
                        className="ms-cm-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FaTimes size={16} />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="ms-cm-body">

                    {/* ─── Header Section ─── */}
                    <div className="ms-cm-section">
                        <div className="ms-cm-section-heading">Header</div>
                        <div className="ms-cm-section-desc">
                            Enter your minisite title and upload a logo.
                        </div>

                        <div className="ms-cm-row-2">
                            {/* Site Name */}
                            <div className="ms-cm-group">
                                <label className="ms-cm-label">
                                    Site Name<span className="ms-cm-required">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`ms-cm-input${errors.title ? ' is-invalid' : ''}`}
                                    placeholder="e.g. HR Site"
                                    value={form.title}
                                    onChange={(e) => handleChange('title', e.target.value)}
                                    autoFocus
                                />
                                {errors.title && (
                                    <div className="ms-cm-error">{errors.title[0]}</div>
                                )}
                            </div>

                            {/* Logo */}
                            <div className="ms-cm-group">
                                <label className="ms-cm-label">
                                    Site Logo<span className="ms-cm-required">*</span>
                                </label>

                                <div
                                    className={[
                                        'ms-cm-logo-upload',
                                        isLogoSet ? 'ms-cm-logo-has-image' : '',
                                        logoUploading ? 'ms-cm-logo-uploading' : '',
                                        (errors.logo || logoError) ? 'is-invalid' : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => !logoUploading && logoInputRef.current?.click()}
                                >
                                    {logoUploading ? (
                                        /* Spinner while temp-upload is in-flight */
                                        <div className="ms-cm-logo-spinner">
                                            <span className="ms-cm-logo-dot" />
                                            <span className="ms-cm-logo-dot" />
                                            <span className="ms-cm-logo-dot" />
                                        </div>
                                    ) : isLogoSet ? (
                                        <img
                                            src={form.logoPreview}
                                            alt="Logo preview"
                                            className="ms-cm-logo-preview"
                                        />
                                    ) : (
                                        <>
                                            <svg
                                                className="ms-cm-logo-icon"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                            >
                                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <path d="M21 15l-5-5L5 21" />
                                            </svg>
                                            <span>Upload Image</span>
                                        </>
                                    )}
                                </div>

                                <input
                                    ref={logoInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="ms-cm-file-input"
                                    onChange={handleLogoChange}
                                />

                                {(errors.logo || logoError) && (
                                    <div className="ms-cm-error">
                                        {logoError || errors.logo[0]}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Team */}
                        {teams.length > 0 && (
                            <div className="ms-cm-group">
                                <label className="ms-cm-label">Team</label>
                                <select
                                    className={`ms-cm-input${errors.team_id ? ' is-invalid' : ''}`}
                                    value={form.team_id}
                                    onChange={(e) => handleChange('team_id', e.target.value)}
                                >
                                    <option value="">Personal (no team)</option>
                                    {teams.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                                {errors.team_id && (
                                    <div className="ms-cm-error">{errors.team_id[0]}</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ─── Footer Section ─── */}
                    <div className="ms-cm-section">
                        <div className="ms-cm-section-heading">Footer</div>
                        <div className="ms-cm-section-desc">
                            Add your contact details so others can easily reach out to you when needed.
                        </div>

                        <div className="ms-cm-row-2">
                            <div className="ms-cm-group">
                                <label className="ms-cm-label">
                                    Email Address<span className="ms-cm-required">*</span>
                                </label>
                                <input
                                    type="email"
                                    className={`ms-cm-input${errors.contact_email ? ' is-invalid' : ''}`}
                                    placeholder="e.g. hr@company.com"
                                    value={form.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                />
                                {errors.contact_email && (
                                    <div className="ms-cm-error">{errors.contact_email[0]}</div>
                                )}
                            </div>

                            <div className="ms-cm-group">
                                <label className="ms-cm-label">
                                    Phone Number<span className="ms-cm-required">*</span>
                                </label>
                                <input
                                    type="tel"
                                    className={`ms-cm-input${errors.contact_phone ? ' is-invalid' : ''}`}
                                    placeholder="e.g. +44 7123 456789"
                                    value={form.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                />
                                {errors.contact_phone && (
                                    <div className="ms-cm-error">{errors.contact_phone[0]}</div>
                                )}
                            </div>
                        </div>

                        <div className="ms-cm-group">
                            <label className="ms-cm-label">
                                Address<span className="ms-cm-required">*</span>
                            </label>
                            <input
                                type="text"
                                className={`ms-cm-input${errors.contact_address ? ' is-invalid' : ''}`}
                                placeholder="Building A, Floor 2"
                                value={form.address}
                                onChange={(e) => handleChange('address', e.target.value)}
                            />
                            {errors.contact_address && (
                                <div className="ms-cm-error">{errors.contact_address[0]}</div>
                            )}
                        </div>
                    </div>

                </div>

                {/* ── Footer ── */}
                <div className="ms-cm-footer">
                    <button
                        type="button"
                        className="ms-cm-btn ms-cm-btn-cancel"
                        onClick={onClose}
                        disabled={loading || logoUploading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="ms-cm-btn ms-cm-btn-done"
                        onClick={handleSubmit}
                        disabled={loading || logoUploading}
                    >
                        {logoUploading
                            ? 'Uploading logo...'
                            : loading
                                ? 'Saving...'
                                : isEdit ? 'Save Changes' : 'Create Minisite'}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default CreateMinisiteModal;
